import React, { useState, useEffect, useRef } from 'react';

// ─── Button ─────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', loading, icon, onClick, disabled, className = '', style = {}, type = 'button' }) {
  const variants = {
    primary: { background: 'var(--gradient-primary)', color: '#fff', border: 'none' },
    secondary: { background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid transparent' },
    danger: { background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' },
    success: { background: '#10b98120', color: '#10b981', border: '1px solid #10b98140' },
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: '13px', borderRadius: '8px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', borderRadius: '10px', gap: '8px' },
    lg: { padding: '13px 24px', fontSize: '15px', borderRadius: '12px', gap: '10px' },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-body)', fontWeight: '600', cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, transition: 'var(--transition)', whiteSpace: 'nowrap',
        ...variants[variant], ...sizes[size], ...style,
      }}
      onMouseEnter={e => { if (!disabled && !loading) e.currentTarget.style.filter = 'brightness(1.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
    >
      {loading ? <Spinner size={14} /> : icon && <span style={{ fontSize: size === 'sm' ? '13px' : '16px' }}>{icon}</span>}
      {children}
    </button>
  );
}

// ─── Badge ──────────────────────────────────────────────
export function Badge({ children, color = '#7c3aed', size = 'sm' }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: size === 'sm' ? '2px 8px' : '4px 12px',
      borderRadius: '999px', fontSize: size === 'sm' ? '11px' : '13px',
      fontWeight: '600', background: `${color}20`, color, border: `1px solid ${color}40`,
    }}>{children}</span>
  );
}

// ─── Card ───────────────────────────────────────────────
export function Card({ children, style = {}, onClick, hoverable, className = '' }) {
  return (
    <div
      onClick={onClick}
      className={`animate-fadeIn ${className}`}
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', padding: '20px',
        transition: 'var(--transition)', cursor: onClick ? 'pointer' : 'default',
        ...(hoverable || onClick ? {} : {}), ...style,
      }}
      onMouseEnter={e => { if (hoverable || onClick) { e.currentTarget.style.borderColor = 'var(--border-light)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; } }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >{children}</div>
  );
}

// ─── Spinner ────────────────────────────────────────────
export function Spinner({ size = 20, color = '#a78bfa' }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `${Math.max(2, size / 10)}px solid ${color}30`,
      borderTopColor: color, animation: 'spin 0.8s linear infinite', flexShrink: 0,
    }} />
  );
}

// ─── Modal ──────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 480 }) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="animate-scaleIn"
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: width,
          maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: '700' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px', lineHeight: 1, padding: '4px', borderRadius: '6px' }}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Toggle ─────────────────────────────────────────────
export function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: '44px', height: '24px', borderRadius: '12px', position: 'relative',
          background: checked ? 'var(--accent-primary)' : 'var(--bg-hover)',
          transition: 'var(--transition)', border: '1px solid var(--border)',
        }}
      >
        <div style={{
          position: 'absolute', top: '2px', left: checked ? '22px' : '2px',
          width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
          transition: 'var(--transition)', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }} />
      </div>
      {label && <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{label}</span>}
    </label>
  );
}

// ─── Input ──────────────────────────────────────────────
export function Input({ label, value, onChange, placeholder, type = 'text', error, icon, multiline, rows = 4, hint, disabled, required }) {
  const [focused, setFocused] = useState(false);
  const inputStyle = {
    width: '100%', background: 'var(--bg-elevated)', border: `1px solid ${error ? 'var(--accent-danger)' : focused ? 'var(--accent-primary)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)',
    fontSize: '14px', outline: 'none', transition: 'var(--transition)', resize: multiline ? 'vertical' : 'none',
    padding: icon ? '10px 12px 10px 36px' : '10px 12px',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>{label}{required && <span style={{ color: 'var(--accent-danger)' }}> *</span>}</label>}
      <div style={{ position: 'relative' }}>
        {icon && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '16px', pointerEvents: 'none' }}>{icon}</span>}
        {multiline
          ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={{ ...inputStyle, padding: '10px 12px' }} />
          : <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={inputStyle} />
        }
      </div>
      {error && <span style={{ fontSize: '12px', color: 'var(--accent-danger)' }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

// ─── Select ─────────────────────────────────────────────
export function Select({ label, value, onChange, options = [], placeholder, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>{label}</label>}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', background: 'var(--bg-elevated)', border: `1px solid ${error ? 'var(--accent-danger)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)',
          fontSize: '14px', padding: '10px 12px', outline: 'none', cursor: 'pointer',
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <span style={{ fontSize: '12px', color: 'var(--accent-danger)' }}>{error}</span>}
    </div>
  );
}

// ─── Empty State ────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '4px' }}>{icon}</div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '700' }}>{title}</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px', maxWidth: '280px', lineHeight: 1.6 }}>{description}</p>
      {action}
    </div>
  );
}

// ─── Stat Card ──────────────────────────────────────────
export function StatCard({ icon, label, value, growth, color = 'var(--accent-primary)' }) {
  return (
    <Card hoverable style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '22px' }}>{icon}</span>
        {growth !== undefined && (
          <Badge color={growth >= 0 ? '#10b981' : '#ef4444'}>
            {growth >= 0 ? '↑' : '↓'} {Math.abs(growth)}%
          </Badge>
        )}
      </div>
      <div>
        <div style={{ fontSize: '28px', fontWeight: '800', fontFamily: 'var(--font-display)', color }}>{typeof value === 'number' ? value.toLocaleString('es') : value}</div>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>{label}</div>
      </div>
    </Card>
  );
}

// ─── Confirm Dialog ─────────────────────────────────────
export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirmar', danger }) {
  return (
    <Modal open={open} onClose={onClose} title={title} width={400}>
      <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>{message}</p>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant={danger ? 'danger' : 'primary'} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</Button>
      </div>
    </Modal>
  );
}

// ─── Network Icon ───────────────────────────────────────
export function NetworkIcon({ network, size = 32 }) {
  const svgs = {
    instagram: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f09433"/>
            <stop offset="25%" stopColor="#e6683c"/>
            <stop offset="50%" stopColor="#dc2743"/>
            <stop offset="75%" stopColor="#cc2366"/>
            <stop offset="100%" stopColor="#bc1888"/>
          </linearGradient>
        </defs>
        <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig)"/>
        <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8" fill="none"/>
        <circle cx="17.2" cy="6.8" r="1.2" fill="white"/>
      </svg>
    ),
    tiktok: (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="6" fill="#010101"/>
        <path d="M16.5 5.5c.4 2 1.7 3 3.5 3.2v2.3c-1.2 0-2.4-.4-3.5-1.1v5c0 2.8-2.2 5-5 5s-5-2.2-5-5 2.2-5 5-5c.2 0 .4 0 .6.1v2.4c-.2 0-.4-.1-.6-.1-1.4 0-2.6 1.2-2.6 2.6s1.2 2.6 2.6 2.6 2.6-1.2 2.6-2.6V5.5h2.4z" fill="white"/>
        <path d="M15.5 5.5c.4 2 1.7 3 3.5 3.2" stroke="#69C9D0" strokeWidth="0.5" fill="none"/>
      </svg>
    ),
    twitter: (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="6" fill="#000000"/>
        <path d="M18 6L13.5 11.5L18.5 18H15L12 14L8.5 18H5.5L10.5 12L5.5 6H9L12 9.5L15 6H18ZM15.5 16.5L7.5 7.5H8.5L16.5 16.5H15.5Z" fill="white"/>
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="6" fill="#0A66C2"/>
        <path d="M7 9h2v8H7V9zm1-1.5A1.25 1.25 0 118 10a1.25 1.25 0 010-2.5zM11 9h2v1.1c.3-.6 1.1-1.2 2.3-1.2C17 8.9 18 10 18 12v5h-2v-4.5c0-1-.4-1.8-1.4-1.8s-1.6.7-1.6 1.8V17h-2V9z" fill="white"/>
      </svg>
    ),
    facebook: (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="6" fill="#1877F2"/>
        <path d="M16 8h-2c-.6 0-1 .4-1 1v2h3l-.5 3H13v7h-3v-7H8v-3h2V9c0-2.2 1.8-4 4-4h2v3z" fill="white"/>
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="6" fill="#FF0000"/>
        <path d="M19.5 7.5s-.2-1.4-.8-2c-.8-.8-1.6-.8-2-.9C14.5 4.5 12 4.5 12 4.5s-2.5 0-4.7.1c-.4.1-1.2.1-2 .9-.6.6-.8 2-.8 2S4.4 9.1 4.4 10.6v1.4c0 1.5.1 3.1.1 3.1s.2 1.4.8 2c.8.8 1.8.8 2.3.9C9 18 12 18 12 18s2.5 0 4.7-.2c.4-.1 1.2-.1 2-.9.6-.6.8-2 .8-2s.1-1.5.1-3.1v-1.4C19.6 9 19.5 7.5 19.5 7.5zM10.5 14v-5l5 2.5-5 2.5z" fill="white"/>
      </svg>
    ),
  };
  const colors = {
    instagram: '#E1306C', tiktok: '#69C9D0', twitter: '#000000',
    linkedin: '#0A66C2', facebook: '#1877F2', youtube: '#FF0000',
  };
  const color = colors[network] || '#888';
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 4,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, overflow: 'hidden',
    }}>
      {svgs[network] || (
        <div style={{ width: size, height: size, borderRadius: size/4, background: `${color}20`, border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.45, color }}>🌐</div>
      )}
    </div>
  );
}

// ─── Tab Bar ────────────────────────────────────────────
export function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: 'var(--radius-md)', width: 'fit-content' }}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: '7px 16px', borderRadius: '8px', border: 'none', fontFamily: 'var(--font-body)',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'var(--transition)',
            background: active === t.id ? 'var(--bg-card)' : 'transparent',
            color: active === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: active === t.id ? 'var(--shadow-sm)' : 'none',
          }}
        >{t.label}</button>
      ))}
    </div>
  );
}

// ─── Progress Bar ───────────────────────────────────────
export function ProgressBar({ value, color = 'var(--accent-primary)', height = 6 }) {
  return (
    <div style={{ width: '100%', height, borderRadius: height, background: 'var(--bg-hover)', overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, value)}%`, height: '100%', background: color, borderRadius: height, transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)' }} />
    </div>
  );
}

// ─── Tooltip ────────────────────────────────────────────
export function Tooltip({ children, text }) {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-hover)', border: '1px solid var(--border)', color: 'var(--text-primary)',
          fontSize: '12px', padding: '5px 10px', borderRadius: '6px', whiteSpace: 'nowrap',
          zIndex: 100, pointerEvents: 'none', boxShadow: 'var(--shadow-sm)',
        }}>{text}</div>
      )}
    </div>
  );
}
